/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Diff viewer panel - right side panel showing file diffs or directory contents
 */

import React from 'react';
import { FileText, FolderOpen, X, Loader, Undo } from 'lucide-react';
import { DiffViewer, type DirectoryFileInfo } from '../DiffViewer';
import type { FileStatus } from './types';

interface DiffViewerPanelProps {
  selectedFile: string | null;
  diff: string;
  fileStatus: FileStatus;
  isNewFile: boolean;
  isDirectory?: boolean;
  directoryFiles?: DirectoryFileInfo[];
  loadingDiff: boolean;
  onClose: () => void;
  onFileClick?: (filePath: string) => void;
  /** Commit hash when viewing a file from commit history */
  commitHash?: string | null;
  /** Handler for reverting a file's changes from a commit */
  onRevertCommitFile?: (filePath: string, commitHash: string) => void;
  /** Whether a commit file revert is in progress */
  revertingCommitFile?: boolean;
}

export function DiffViewerPanel({
  selectedFile,
  diff,
  fileStatus,
  isNewFile,
  isDirectory = false,
  directoryFiles,
  loadingDiff,
  onClose,
  onFileClick,
  commitHash,
  onRevertCommitFile,
  revertingCommitFile = false
}: DiffViewerPanelProps) {
  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <FileText size={48} className="mx-auto mb-4" />
          <p>Select a file to view changes</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-4 changes-page-panel-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isDirectory ? (
              <FolderOpen size={16} className="text-yellow-600" />
            ) : (
              <FileText size={16} />
            )}
            <h3 className="font-medium">{selectedFile}</h3>
            {isDirectory && (
              <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                Directory
              </span>
            )}
            {!isDirectory && isNewFile && (
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                New File
              </span>
            )}
            {!isDirectory && fileStatus === 'deleted' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                Deleted
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {commitHash && onRevertCommitFile && !isDirectory && (
              <button
                onClick={() => onRevertCommitFile(selectedFile, commitHash)}
                disabled={revertingCommitFile}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {revertingCommitFile ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Reverting...
                  </>
                ) : (
                  <>
                    <Undo size={14} />
                    Revert
                  </>
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto p-4">
        {loadingDiff ? (
          <div className="flex items-center justify-center h-32">
            <Loader size={24} className="animate-spin text-blue-600" />
          </div>
        ) : isDirectory ? (
          <div className="bg-white rounded changes-page-container overflow-x-auto overflow-y-auto">
            <DiffViewer
              diff=""
              filePath={selectedFile}
              fileStatus={fileStatus}
              isNewFile={false}
              isDirectory={true}
              directoryFiles={directoryFiles}
              onFileClick={onFileClick}
            />
          </div>
        ) : diff ? (
          <div className="bg-white rounded changes-page-container overflow-x-auto overflow-y-auto">
            <DiffViewer
              diff={diff}
              filePath={selectedFile}
              fileStatus={fileStatus}
              isNewFile={isNewFile}
            />
          </div>
        ) : (
          <div className="text-center text-gray-500 mt-8">
            No diff available
          </div>
        )}
      </div>
    </>
  );
}

